import React, { useEffect, useState } from 'react';
import { Button } from '../components/UI/button';
import { Card } from '../components/UI/card';
import {
  Phone, Mail, Shield, Users, Clock, Award,
  Zap, Target, ChevronRight, Factory, Recycle,
  CheckCircle, ArrowRight, Linkedin, Instagram, Facebook
} from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';

import logo from '../assets/FLUXLAB-Logo.png';
import img1 from '../assets/products/fluxlab1.jpg';
import img2 from '../assets/products/Fluxlab2.jpg';
import img3 from '../assets/products/fluxlab3.jpg';
import img4 from '../assets/automotive-assembly.jpg';


import plastic1 from "../assets/products/Plasticpart.png"
import plastic2 from "../assets/products/plasticpart-2.png"
import plastic3 from "../assets/products/PlasticPart-3.png"
import plastic4 from "../assets/products/PlasticPart-4.png"
import plastic5 from "../assets/products/PlasticPart-5.png"
import plastic6 from "../assets/products/PlasticPart-6.png"
import plastic7 from "../assets/products/PlasticPart-7.png"
import plastic8 from "../assets/products/PlasticPart-8.png"


import handlerelease from "../assets/products/HandleRelease.png"
import hookovermold from "../assets/products/HookOvermold.png"
import housinglh from "../assets/products/Housing-LH.png"
import housing from "../assets/products/Housing.png"
import flexmate from "../assets/products/Flexmate.png"
import lockovermold from "../assets/products/LockOvermold.png"
import mainhub from "../assets/products/Mainhub.png"
import motorbridge from "../assets/products/motorbridge.png"

// 🧩 Local material images
import polypropyleneImg from '../assets/objects-printed-by-d-printer-objects-printed-d-printer-table.jpg';
import polyethyleneImg from '../assets/close-up-car-care-process.jpg';
import polycarbonateImg from '../assets/polyc.jpg';

import hero1 from "../assets/products/hero1.avif";
import hero2 from "../assets/products/hero2.avif";
import hero3 from "../assets/products/hero3.jpg";


const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const carouselImages = [hero1, hero2, hero3];

const [currentIndex, setCurrentIndex] = useState(0);

useEffect(() => {
  const interval = setInterval(() => {
    setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
  }, 3000); // 3 seconds

  return () => clearInterval(interval);
}, []);

  const { scrollYProgress } = useScroll();
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.7]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    {
      title: "Polypropylene",
      description:
        "Premium quality raw plastics ensuring durable and long-lasting performance. Leading suppliers for plastic components in the automotive industry.",
      image: polypropyleneImg, // 🆕 local imported image
      features: ["High Durability", "Heat Resistant", "Chemical Stability"],
    },
    {
      title: "Polyethylene",
      description:
        "Top-notch manufacturing precision for plastic parts in various sizes and complex dimensions meeting automotive industry specifications.",
      image: polyethyleneImg, // 🧩 local imported image
      features: ["Impact Resistant", "Flexible Design", "Lightweight"],
    },
    {
      title: "Polycarbonate",
      description:
        "High efficiency products from well-trained production teams. Vast knowledge in plastic components manufacturing delivering excellence.",
      image: polycarbonateImg, // 🆕 local imported image
      features: ["Ultra Strong", "Optical Clarity", "UV Resistant"],
    },
  ];


  const processServices = [
    {
      title: "Injection Molding",
      description:
        "Fluxlab delivers precision-engineered injection molding solutions using advanced machinery and high-performance thermoplastics, ensuring consistent quality, dimensional accuracy, and cost-effective production for diverse industrial applications.",
    },
    {
      title: "Blow Molding",
      description:
        "Our blow molding expertise produces lightweight, durable, and high-strength hollow plastic components tailored for automotive, pharmaceutical, and consumer industries with superior consistency and performance.",
    },
    {
      title: "Compression Molding",
      description:
        "Fluxlab specializes in compression molding of advanced polymers, creating robust, complex-shaped components with excellent strength, precision, and heat resistance for demanding engineering applications.",
    },
    {
      title: "Prototype Development",
      description:
        "We provide rapid prototype development to validate designs, optimize materials, and accelerate product launches, bridging concept to production with accuracy, innovation, and reduced lead time.",
    },
    {
      title: "Rotational Molding",
      description:
        "Fluxlab’s rotational molding technology enables the production of seamless, stress-free, and complex hollow parts with uniform wall thickness, ideal for high-durability industrial and consumer applications.",
    },
    {
      title: "Design Optimization",
      description:
        "Through design optimization, our experts refine 3D models, materials, and tooling for maximum efficiency—enhancing performance, manufacturability, and product longevity across every molding process.",
    },
  ];

  const features = [
    { icon: Users, title: "Experienced Workers", description: "Skilled team with years of expertise in automotive plastic manufacturing" },
    { icon: Shield, title: "Quality Assurance", description: "Rigorous testing ensuring every component meets industry standards" },
    { icon: Clock, title: "24/7 Customer Support", description: "Round-the-clock assistance for all your manufacturing needs" },
    { icon: Factory, title: "Advanced Manufacturing", description: "State-of-the-art facilities with cutting-edge technology" },
    { icon: Recycle, title: "Sustainable Solutions", description: "Eco-friendly, recyclable materials for a better tomorrow" },
    { icon: Target, title: "Precision Engineering", description: "Complex dimensions and specifications executed flawlessly" },
  ];

  return (
    <div className="min-h-screen bg-[white]">
      {/* Navigation */}
     {/* HEADER SECTION */}
<motion.nav
  className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
  style={{ opacity: headerOpacity }}
  initial={{ y: -100 }}
  animate={{ y: 0 }}
  transition={{ duration: 0.6 }}
>
  <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
    {/* 🟢 Logo Section */}
    <div
      className="flex items-center gap-3 cursor-pointer"
      onClick={scrollToTop}
    >
      <img
        src={logo}
        alt="Company Logo"
        className="w-[110px] h-auto" // Increased by 30%
      />
    </div>

    {/* 🔵 Navigation Links */}
    <div className="hidden md:flex items-center gap-8">
      <a
        href="#about"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        About
      </a>
      <a
        href="#services"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Services
      </a>
      <a
        href="#products"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Products
      </a>
      {/* <a
  href="/products"
  className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
>
  Products
</a> */}

      <a
        href="#why-us"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Why Us
      </a>
      <a
        href="#contact"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Contact
      </a>
    </div>

    {/* 🔴 Call-to-Action Button */}
    <a href="#contact">
      <Button className="bg-[#2563eb] hover:bg-red-700 text-white flex items-center">
        <Phone className="w-4 h-4 mr-2" />
        Get Quote
      </Button>
    </a>
  </div>
</motion.nav>

{/* HERO SECTION */}
{/* HERO SECTION WITH LOCAL IMAGE CAROUSEL */}
<section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">

  {/* 🔵 Background Carousel */}
  <div className="absolute inset-0 z-0">
    {carouselImages.map((img, index) => (
      <div
        key={index}
        className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out 
          ${index === currentIndex ? "opacity-100" : "opacity-0"}`}
        style={{
          backgroundImage: `url(${img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translateY(${scrollY * 0.5}px)` // keep parallax effect
        }}
      />
    ))}

    {/* Dark overlay */}
    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-slate-900/80 to-slate-900/90"></div>
  </div>

  {/* 🔵 Foreground Content */}
  <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <div className="inline-block mb-4 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full">
        <span className="text-blue-300 text-sm font-medium">
          Precision Manufacturing Since 2023
        </span>
      </div>

      <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
        Perfect Plastic Parts<br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
          For Automotive Excellence
        </span>
      </h1>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a href="#services">
          <Button size="lg" className="bg-blue-600 hover:bg-red-700 text-white text-lg px-8 py-6">
            Explore Services <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </a>

        <a href="#contact">
          <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10 text-lg px-8 py-6">
            Contact Us <Phone className="ml-2 w-5 h-5" />
          </Button>
        </a>
      </div>
    </motion.div>
  </div>
</section>



 

      {/* About Section */} 

      <section id="about" className="py-24 bg-white"> 

        <div className="max-w-7xl mx-auto px-6"> 

          <div className="grid md:grid-cols-2 gap-12 items-center"> 
<motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-left"
        >
          <div className="text-blue-600 font-semibold mb-3 flex items-center gap-2">
            <Award className="w-5 h-5" />
            About FLUXLAB
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            Leader In Automotive Plastic Manufacturing
          </h2>

          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            We are a leading manufacturer of durable, lightweight, and recyclable plastic components,
            serving clients in India and abroad with years of industry expertise.
          </p>

          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            FLUXLAB has been innovating fiber-reinforced plastic polymers that are shock-absorbing,
            lightweight for easier storage, and incredibly durable.
          </p>

          <div className="flex items-center gap-6 mb-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">10+</div>
              <div className="text-slate-600 text-sm">Years of Experience</div>
            </div>

            <div className="w-px h-16 bg-slate-200"></div>

            <div>
              <div className="flex items-center gap-2 text-slate-700 mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                ISO Certified Manufacturing
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Global Quality Standards
              </div>
            </div>
          </div>

          {/* <Button
            size="lg"
            className="bg-blue-600 hover:bg-red-700 text-white"
          >
            Learn More <ChevronRight className="ml-2 w-5 h-5" />
          </Button> */}
        </motion.div>

        {/* RIGHT SIDE — IMAGES GRID */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4"
        >
          {/* Replace these with your imported images */}
          <img
            src={img1}
            alt="Factory 1"
            className="rounded-2xl object-cover w-full h-64 shadow-md"
          />
          <img
            src={img2}
            alt="Factory 2"
            className="rounded-2xl object-cover w-full h-64 shadow-md mt-6"
          />
          <img
            src={img3}
            alt="Factory 3"
            className="rounded-2xl object-cover w-full h-64 shadow-md"
          />
          <img
            src={img4}
            alt="Factory 4"
            className="rounded-2xl object-cover w-full h-64 shadow-md mt-6"
          />
        </motion.div>

 

            <motion.div 

              initial={{ opacity: 0, x: 50 }} 

              whileInView={{ opacity: 1, x: 0 }} 

              transition={{ duration: 0.8 }} 

              viewport={{ once: true }} 

              className="relative" 

            > 

                {/* <div className="grid grid-cols-2 gap-4"> 

                  <div className="space-y-4"> 

                    <img  

                      src="https://images.unsplash.com/photo-1581092918484-8313e1f6d559?w=600&auto=format&fit=crop"  

                      alt="Manufacturing"  

                      className="rounded-2xl shadow-2xl w-full h-64 object-cover" 

                    /> 

                    <img  

                      src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&auto=format&fit=crop"  

                      alt="Precision"  

                      className="rounded-2xl shadow-2xl w-full h-48 object-cover" 

                    /> 

                  </div> 

                  <div className="space-y-4 pt-8"> 

                    <img  

                      src="https://images.unsplash.com/photo-1581092918484-8313e1f6d559?w=600&auto=format&fit=crop"  

                      alt="Quality"  

                      className="rounded-2xl shadow-2xl w-full h-48 object-cover" 

                    /> 

                    <img  

                      src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=600&auto=format&fit=crop"  

                      alt="Innovation"  

                      className="rounded-2xl shadow-2xl w-full h-64 object-cover" 

                    /> 

                  </div> 

                </div>  */}

              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-600/10 rounded-3xl -z-10"></div> 

              <div className="absolute -top-6 -right-6 w-32 h-32 bg-cyan-600/10 rounded-3xl -z-10"></div> 

            </motion.div> 

          </div> 

        </div> 

      </section> 

 

      {/* Services Section */} 

      <section id="materials" className="py-24 bg-slate-50"> 

        <div className="max-w-7xl mx-auto px-6"> 

          <motion.div  

            className="text-center mb-16" 

            initial={{ opacity: 0, y: 30 }} 

            whileInView={{ opacity: 1, y: 0 }} 

            transition={{ duration: 0.6 }} 

            viewport={{ once: true }} 

          > 

            <div className="text-blue-600 font-semibold mb-3">Our Materials</div> 

            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4"> 

              Premium Plastic Solutions 

            </h2> 

            <p className="text-xl text-slate-600 max-w-2xl mx-auto"> 

              Industry-leading materials engineered for automotive excellence 

            </p> 

          </motion.div> 

 

          <div className="grid md:grid-cols-3 gap-8"> 

            {services.map((service, index) => ( 

              <motion.div
      key={index}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-0 h-full">
        <div className="relative h-64 overflow-hidden">
          <img
            src={
              service.title.toLowerCase().includes("polyethylene")
                ? polyethyleneImg // 👈 use imported image for Polyethylene
                : service.image // otherwise use default image
            }
            alt={service.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
          <div className="absolute bottom-4 left-4">
            <h3 className="text-2xl font-bold text-white mb-1">{service.title}</h3>
          </div>
        </div>

        <div className="p-6 text-left"> {/* 👈 Left-aligned content */}
          <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>

          <div className="space-y-2 mb-6">
            {service.features.map((feature, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 text-sm text-slate-700"
              >
                <Zap className="w-4 h-4 text-blue-600" />
                {feature}
              </div>
            ))}
          </div>

          {/* <Button
            variant="ghost"
            className="w-full group-hover:bg-red-600 group-hover:text-white transition-colors"
          >
            Learn More <ChevronRight className="ml-2 w-4 h-4" />
          </Button> */}
        </div>
      </Card>
    </motion.div>

            ))} 

          </div> 

        </div> 

      </section> 

 {/* 🔵 New OUR SERVICES Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-blue-600 font-semibold mb-3">
              Our Services
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Advanced Molding & Design Solutions
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive molding, prototyping, and design optimization under one roof.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {processServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="group p-8 hover:shadow-2xl transition-all duration-300 border border-slate-100 hover:border-blue-500 bg-white h-full rounded-2xl hover:scale-[1.03]">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {service.description}
                  </p>
                 
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


{/* -----------------------------------------------------------
     OUR PRODUCTS SECTION (NEW)
------------------------------------------------------------ */}
<section id="products" className="py-24 bg-slate-50">
  <div className="max-w-7xl mx-auto px-6">

    {/* Section Header */}
    <motion.div
      className="text-center mb-16"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      <div className="text-blue-600 font-semibold mb-3">Our Products</div>
      <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
        Precision Plastic Components
      </h2>
      <p className="text-xl text-slate-600 max-w-2xl mx-auto">
        Explore our range of high-quality molded parts engineered for automotive excellence.
      </p>
    </motion.div>

    {/* PLASTIC PARTS */}
    <h3 className="text-2xl font-bold text-slate-900 mb-6">Plastic Parts</h3>

    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10 mb-20">

      {[
        { 
          name: "Handle Release", 
          image: handlerelease,
          desc: "Premium two-shot overmolded components with multi-color integration, precision pad printing, and textured graining for enhanced aesthetics and tactile experience."
        },
        { 
          name: "Hook Overmould", 
          image: hookovermold,
          desc: "High-performance Hytrel overmolded metal inserts offering exceptional toughness, flexibility, and resistance to impact and abrasion."
        },
        { 
          name: "Housing", 
          image: housing,
          desc: "Lightweight, corrosion-resistant Polypropylene parts with optimized rib design and minimal thickness for durable and cost-effective performance."
        },
        { 
          name: "Housing-LH", 
          image: housinglh,
          desc: "Precision-engineered plastic parts featuring multiple horizontal slots, crafted using advanced slider technology and hot runner systems."
        },
        { 
          name: "Lock Overmould", 
          image: lockovermold,
          desc: "Robust overmolded components with a large plastic envelope around metal inserts, providing superior strength and long-term durability."
        },
        { 
          name: "FlexMate", 
          image: flexmate,
          desc: "Expertly crafted overmolded wire assemblies ensuring strong weldability, vibration resistance, and reliable electrical insulation."
        },
        { 
          name: "Main Hub", 
          image: mainhub,
          desc: "Miniature precision components crafted to tight tolerances, ideal for applications requiring detailed geometry and consistent quality."
        },
        { 
          name: "Motor Bridge", 
          image: motorbridge,
          desc: "High-performance Nylon components reinforced with Glass Fiber for exceptional strength, lightweight structure, and long-term sustainability."
        },
      ].map((product, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 border border-slate-200 rounded-2xl bg-white h-full">

            {/* Product Image */}
            <div className="w-full h-48 bg-slate-100 overflow-hidden">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  Image Coming Soon
                </div>
              )}
            </div>

            {/* Product Content */}
            <div className="p-5 text-left">
              <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                {product.name}
              </h3>

              <p className="text-slate-600 text-sm mb-4">
                {product.desc}
              </p>

            </div>

          </Card>
        </motion.div>
      ))}
    </div>

    {/* OTHER PRODUCTS */}
    <h3 className="text-2xl font-bold text-slate-900 mb-6">Other Products</h3>

    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10">

      {[
        { 
          name: "Release CAM", 
          image: plastic1,
          desc: "Precision-crafted circular components designed for smooth operation, accurate geometry, and reliable mechanical performance."
        },
        { 
          name: "Strap Bezel", 
          image: plastic2,
          desc: "Precision-engineered PC components with uniform thickness, ensuring transparency, impact resistance, and dimensional stability."
        },
        { 
          name: "Sleeve Bracket", 
          image: plastic3,
          desc: "Plastic components with cantilever ribs, designed for angled applications requiring structural integrity and flexibility."
        },
        { 
          name: "Bearing Cover", 
          image: plastic4,
          desc: "Customized Nylon Glass Fiber components featuring ribs on curved edges for superior strength, durability, and lightweight performance."
        },
        { 
          name: "Sleeve", 
          image: plastic5,
          desc: "High-performance POM components reinforced with Glass Fiber for improved stiffness, strength, and wear resistance."
        },
        { 
          name: "Bracket Locator", 
          image: plastic6,
          desc: "Precision Nylon components with thin, long-distance ribs providing stability and strength for complex geometries."
        },
        { 
          name: "Striker", 
          image: plastic7,
          desc: "Glass Fiber reinforced Nylon parts featuring 90° long ribs for exceptional durability, stiffness, and resistance to deformation."
        },
        { 
          name: "Switch Cover", 
          image: plastic8,
          desc: "Robust Nylon components featuring thick cantilever ribs for superior load-bearing performance and deflection resistance."
        },
      ].map((product, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 border border-slate-200 rounded-2xl bg-white h-full">

            {/* Image */}
            <div className="w-full h-48 bg-slate-100 overflow-hidden">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  Image Coming Soon
                </div>
              )}
            </div>

            {/* Text */}
            <div className="p-5 text-left">
              <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                {product.name}
              </h3>

              <p className="text-slate-600 text-sm mb-4">
                {product.desc}
              </p>

            </div>

          </Card>
        </motion.div>
      ))}
    </div>

  </div>
</section>




      {/* Why Choose Us */} 

      <section id="why-us" className="py-24 bg-white">
  <div className="max-w-7xl mx-auto px-6">
    <motion.div
      className="text-center mb-16"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      <div className="text-blue-600 font-semibold mb-3">Why Choose FLUXLAB</div>

      <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
        Excellence In Every Component
      </h2>

      <p className="text-xl text-slate-600 max-w-2xl mx-auto">
        Reasons why industry leaders trust us for their manufacturing needs
      </p>
    </motion.div>

    <div className="grid md:grid-cols-3 gap-8">
      {features.map((feature, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          <Card className="p-8 hover:shadow-2xl transition-all duration-300 border border-transparent hover:border-blue-500 bg-white h-full rounded-2xl hover:scale-[1.03] flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 shadow-md">
              <feature.icon className="w-8 h-8 text-white" />
            </div>

            <h3 className="text-xl font-bold text-black mb-3">
              {feature.title}
            </h3>

            <p className="text-slate-600 leading-relaxed">
              {feature.description}
            </p>
          </Card>
        </motion.div>
      ))}
    </div>
  </div>
</section>


 

      {/* Contact Section */} 

      <section id="contact" className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
  <div className="max-w-7xl mx-auto px-6">
    <div className="grid md:grid-cols-2 gap-12 items-center">
      
      {/* LEFT COLUMN */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="text-left"
      >
        <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
          Ready To Start Your<br />
          <span className="text-blue-400">Manufacturing Journey?</span>
        </h2>

        <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-xl">
          Get in touch with our team to discuss your project requirements and discover  
          how FLUXLAB can bring your vision to life.
        </p>

        <div className="space-y-4">
          <a href="tel:+919099159922" className="flex items-center gap-4 group">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <div className="text-sm text-slate-400">Call Us</div>
              <div className="text-xl font-semibold">+91 9099159922</div>
            </div>
          </a>

          <a href="mailto:sales@fluxlab.in" className="flex items-center gap-4 group">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Mail className="w-6 h-6" />
            </div>
            <div>
              <div className="text-sm text-slate-400">Email Us</div>
              <div className="text-xl font-semibold">sales@fluxlab.in</div>
            </div>
          </a>
        </div>
      </motion.div>

      {/* RIGHT COLUMN - FORM */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Card className="p-8 bg-white/5 backdrop-blur-md border border-white/10 text-left">
          <h3 className="text-2xl font-bold mb-6">Send Us A Message</h3>
          <form className="space-y-4">
            <div>
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <textarea
                rows="4"
                placeholder="Your Message"
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              ></textarea>
            </div>
            <Button size="lg" className="w-full bg-blue-600 hover:bg-red-700 text-white">
              Send Message <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>
        </Card>
      </motion.div>
    </div>
  </div>
</section>


 

      {/* Footer */} 

     <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* ---- Company Info ---- */}
            <div className="text-left">
              <div
                className="flex items-center gap-3 mb-4 cursor-pointer"
                onClick={scrollToTop}
              >
                <img
                  src={logo}
                  alt="FLUXLAB Logo"
                  className="w-[110px] h-auto"
                />
              </div>

              <p className="text-sm leading-relaxed mb-4">
                Leading manufacturer of automotive plastic components with a
                decade of excellence.
              </p>

              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/fluxlabin/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="FLUXLAB on LinkedIn"
                  className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a
                  href="https://www.instagram.com/fluxlab.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="FLUXLAB on Instagram"
                  className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.facebook.com/fluxlab.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="FLUXLAB on Facebook"
                  className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* ---- Quick Links ---- */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="#about"
                    className="hover:text-white transition-colors inline-block"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href="#services"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Services
                  </a>
                </li>
                <li>
                  <a
                    href="#why-us"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Why Choose Us
                  </a>
                </li>
                <li>
                  <a
                    href="#contact"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* ---- Legal Links ---- */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="/terms"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>

            {/* ---- Contact Info ---- */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>+91 9099159922</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>sales@fluxlab.in</span>
                </li>
              </ul>
            </div>
          </div>

          {/* ---- Footer Bottom ---- */}
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>
              &copy; {new Date().getFullYear()} FLUXLAB. All rights reserved. | Precision Manufacturing Excellence
            </p>
          </div>
        </div>
      </footer>

    </div> 

  ); 

} 

export default Home;
